import AppointmentSelector from "@components/Booking/DoubleBooking/AppointmentSelector";
import Heading from "@components/Booking/Heading";
import Icon from "@components/Icon";
import {buildBookingLink, parseBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {
  DuplicateAppointment,
  useFetchDuplicateAppointments,
} from "@services/graphql/queries/appointment";
import {MonolithLocationFields} from "@services/graphql/queries/location";
import {useTranslation} from "@i18n/client";
import React, {useCallback, useMemo} from "react";

import {I18nNamespace} from "@i18n";
import {useRouter} from "next/router";
import {useBookAndGoToConfirmation} from "@page-containers/booking/utils/useBookAndGoToConfirmation";
import AppointmentSelectorGlimmer from "@components/Booking/DoubleBooking/AppointmentSelectorGlimmer";

type Props = {
  apptReasonId: string;
  location: MonolithLocationFields;
  selectedPatientId: string;
  selectedTime: number;
  selectedSlotIds: string[];
  specialtyId: string;
  dropoutVisitorId?: string;
};
const DoubleBookingScreen = (props: Props) => {
  const {duplicate, loading} = useDuplicateAppointment(
    props.apptReasonId,
    props.location.practiceId,
    props.selectedPatientId,
    props.selectedSlotIds,
    props.specialtyId,
  );

  const attemptingToBook = useMemo(
    () => ({
      slotTime: props.selectedTime,
      locationName: props.location.name,
      locationTimezone: props.location.timezone,
      addressFirstLine: props.location.address?.firstLine,
      addressCity: props.location.address?.city,
      addressState: props.location.address?.state,
    }),
    [props],
  );

  const onConfirmExisting = useOnConfirmExisting(duplicate);
  const onReschedule = useOnReschedule(props, duplicate?.id);

  return (
    <div className="flex flex-col gap-10 items-center">
      <Context />
      {loading || !duplicate ? (
        <AppointmentSelectorGlimmer />
      ) : (
        <AppointmentSelector
          existingAppointment={duplicate}
          attemptingToBook={attemptingToBook}
          loading={false}
          onConfirmExisting={onConfirmExisting}
          onReschedule={onReschedule}
        />
      )}
      <Explanation />
    </div>
  );
};

function useDuplicateAppointment(
  apptReasonId: string,
  practiceId: string,
  patientId: string,
  selectedSlotIds: string[],
  specialtyId: string,
) {
  const {data, loading, error} = useFetchDuplicateAppointments({
    input: {
      patientId,
      practiceId,
      apptReasonId,
      specialtyId,
      slotIds: selectedSlotIds,
    },
  });
  const duplicate = data?.duplicateAppointments?.duplicates?.[0];
  return {duplicate, loading, error};
}

export default React.memo(DoubleBookingScreen);

const InternalContext = () => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Icon
        icon="exclamation-circle"
        className="text-[2rem] w-16 h-16 bg-yellow-100 items-center justify-center inline-flex rounded-full"
      />
      <div className="gap-2">
        <Heading>
          {i18n.t("This looks like a duplicate appointment", {ns: "web-scheduling"})}
        </Heading>
        <div className="mt-4 body-md tac max-w-[16rem] sm:max-w-none">
          {i18n.t("Would you like to reschedule?", {ns: "web-scheduling"})}
        </div>
      </div>
    </div>
  );
};
const Context = React.memo(InternalContext);

const InternalExplanation = () => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  return (
    <span className="text-sm text-center text-gray-700">
      {i18n.t(
        "Booking duplicate appointments limits appointment availability for other patients. If you have questions or need help scheduling, call 415-707-3197.",
        {ns: "web-scheduling"},
      )}
    </span>
  );
};
const Explanation = React.memo(InternalExplanation);

function useOnConfirmExisting(existing?: DuplicateAppointment) {
  const router = useRouter();
  const currentQuery = parseBookingQuery(router.query);
  return useCallback(() => {
    if (!existing) {
      return;
    }
    router.push(
      buildBookingLink({
        ...currentQuery,
        appointmentId: existing.id,
        locationSlug: existing.locationSlug,
        selectedTime: existing.slotTime,
        rescheduling: false,
        isSelectedTimeUnavailable: false,
      }),
    );
  }, [existing, router, currentQuery]);
}

const rescheduling = {rescheduling: true};
function useOnReschedule(props: Props, existingApptId?: string) {
  const router = useRouter();
  const currentQuery = parseBookingQuery(router.query);

  const bookOrReschedule = useBookAndGoToConfirmation(rescheduling, props.dropoutVisitorId);
  return useCallback(async () => {
    await bookOrReschedule(
      {
        reschedulingApptId: existingApptId,
        virtual: false,
        apptReasonId: props.apptReasonId,
        locationId: props.location.id,
        patientId: props.selectedPatientId,
        practiceId: props.location.practiceId,
        slotIds: props.selectedSlotIds,
        specialtyId: props.specialtyId,
      },
      {
        locationSlug: currentQuery.locationSlug,
        selectedTime: currentQuery.selectedTime,
      },
    );
  }, [bookOrReschedule, currentQuery, existingApptId, props]);
}
